// The digestible Today view: what's happening NOW, what's NEXT, the day as one strip,
// and the rest in small chunks (morning / afternoon / evening). Built for low cognitive load.
import { Check, ChevronDown, ChevronRight, Flag, MapPin, Navigation, PartyPopper, Sunrise } from 'lucide-react';
import { useMemo, useState } from 'react';
import { dayPart, DAY_PART_LABEL, roomLabel, shortTitle, type CalendarItem, type DayPart, type NextUp } from '@tempo/core';
import { api } from '../api';
import { MODE_LABEL, dateOf, dt, duration, fromISODate, time } from '../lib/format';
import { useAction, useNow, usePlaces, useSettings } from '../lib/hooks';
import { ItemIcon, ModeIcon } from './kinds';
import { Button } from './ui';

type Item = CalendarItem;

/** Where to show for an item: the room for lectures, a place name if it isn't home, nothing for home. */
function useWhere() {
  const places = usePlaces().data ?? [];
  const home = useSettings().data?.planner.homePlaceId ?? null;
  return (it: Item): string | null => {
    if (it.kind === 'event' && it.location) {
      const room = roomLabel(it.location);
      const building = places.find((p) => p.id === it.placeId)?.name.split(' · ')[1];
      return building && room && !room.includes(building) ? `${room} · ${building}` : room;
    }
    if (!it.placeId || it.placeId === home) return null;
    return places.find((p) => p.id === it.placeId)?.name ?? null;
  };
}

const label = (it: Item) => shortTitle(it);
const canTick = (it: Item) => it.kind === 'routine' || it.kind === 'task';

function Tick({ item }: { item: Item }) {
  const done = item.status === 'done';
  const toggle = useAction(
    () => api.post(`/blocks/${encodeURIComponent(item.id)}/status`, { status: done ? 'planned' : 'done' }),
    done ? undefined : 'Done ✓',
  );
  return (
    <button
      className={`tick ${done ? 'on' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        toggle.mutate();
      }}
      aria-label={done ? 'Mark not done' : 'Mark done'}
      disabled={toggle.isPending}
    >
      <Check size={16} strokeWidth={3} />
    </button>
  );
}

export function FocusDay({
  date,
  items,
  isToday,
  next,
  onSelect,
}: {
  date: string;
  items: Item[];
  isToday: boolean;
  next: NextUp | undefined;
  onSelect: (i: Item) => void;
}) {
  const now = useNow(30_000);
  const where = useWhere();

  const timed = useMemo(
    () => items.filter((i) => i.kind !== 'sleep' && i.kind !== 'deadline' && !i.allDay && dateOf(i.start) === date).sort((a, b) => a.start - b.start),
    [items, date],
  );
  const things = timed.filter((i) => i.kind !== 'travel');
  const travelFor = new Map(timed.filter((i) => i.kind === 'travel' && i.targetId).map((i) => [i.targetId!, i]));
  const deadlines = items.filter((i) => i.kind === 'deadline' && dateOf(i.start) === date);
  const bed = items.find((i) => i.kind === 'sleep' && dateOf(i.start) === date);
  const asleep = isToday ? items.find((i) => i.kind === 'sleep' && i.start <= now && now < i.end) ?? null : null;

  const current = isToday ? things.find((i) => i.start <= now && now < i.end) ?? null : null;
  const onTheWay = isToday ? timed.find((i) => i.kind === 'travel' && i.start <= now && now < i.end) ?? null : null;
  const upcoming = isToday ? things.filter((i) => i.start > now) : things;
  const nextItem = isToday ? upcoming[0] ?? null : null;
  const done = isToday ? things.filter((i) => i.end <= now) : [];
  const later = isToday ? upcoming.slice(1) : things;

  return (
    <div className="focus-day">
      {deadlines.length > 0 && (
        <div className="due-row">
          {deadlines.map((d) => (
            <button key={d.id} className="due-chip" onClick={() => onSelect(d)}>
              <Flag size={13} /> Due {time(d.start)} · {label(d).title.replace(/^Due:\s*/, '')}
            </button>
          ))}
        </div>
      )}

      {isToday && (
        <NowCard current={current} onTheWay={onTheWay} asleep={asleep} nextItem={nextItem} lastEnd={done.at(-1)?.end ?? null} bed={bed} now={now} where={where} onSelect={onSelect} items={items} />
      )}
      {isToday && nextItem && <NextCard item={nextItem} travel={travelFor.get(nextItem.id) ?? null} next={next} now={now} where={where} onSelect={onSelect} />}

      <DayStrip things={timed} date={date} now={isToday ? now : null} onSelect={onSelect} />

      <Chunks
        later={later}
        done={done}
        isToday={isToday}
        now={now}
        where={where}
        travelFor={travelFor}
        onSelect={onSelect}
        heading={isToday ? 'Later today' : 'The day'}
      />
      {bed && <div className="bed-line">Bed {time(bed.start)}</div>}
    </div>
  );
}

// ───────────────────────────── NOW ─────────────────────────────

function NowCard({
  current,
  onTheWay,
  asleep,
  nextItem,
  lastEnd,
  bed,
  now,
  where,
  items,
  onSelect,
}: {
  current: Item | null;
  onTheWay: Item | null;
  asleep: Item | null;
  nextItem: Item | null;
  lastEnd: number | null;
  bed: Item | undefined;
  now: number;
  where: (i: Item) => string | null;
  items: Item[];
  onSelect: (i: Item) => void;
}) {
  if (onTheWay) {
    const target = items.find((i) => i.id === onTheWay.targetId);
    const left = onTheWay.end - now;
    return (
      <div className="now-card" style={{ ['--c' as string]: 'var(--accent)' }}>
        <div className="now-label">On the way</div>
        <div className="now-title">
          <ModeIcon mode={onTheWay.mode} size={26} /> {target ? label(target).title : onTheWay.title.replace(/^Travel to /, '')}
        </div>
        <Progress start={onTheWay.start} end={onTheWay.end} now={now} />
        <div className="now-meta">
          Arrive by <b>{time(onTheWay.end)}</b> · {duration(left)} to go
        </div>
        {onTheWay.directionsUrl && (
          <a className="btn primary" href={onTheWay.directionsUrl} target="_blank" rel="noreferrer">
            <Navigation size={16} /> Directions
          </a>
        )}
      </div>
    );
  }

  if (current) {
    const l = label(current);
    const place = where(current);
    return (
      <div className="now-card" style={{ ['--c' as string]: current.color }} onClick={() => onSelect(current)}>
        <div className="now-label">Now</div>
        <div className="now-title">
          <span className="now-icon">
            <ItemIcon item={current} size={26} />
          </span>
          {l.title}
        </div>
        {(place || l.detail) && (
          <div className="now-sub">
            {place && (
              <span>
                <MapPin size={14} /> {place}
              </span>
            )}
            {l.detail && <span className="faint">{l.detail}</span>}
          </div>
        )}
        <Progress start={current.start} end={current.end} now={now} />
        <div className="now-meta">
          <b>{duration(current.end - now)}</b> left · until {time(current.end)}
        </div>
        {canTick(current) && current.status !== 'done' && (
          <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 12 }}>
            <DoneButton item={current} />
          </div>
        )}
      </div>
    );
  }

  if (asleep && !current) {
    return (
      <div className="now-card free" style={{ ['--c' as string]: 'var(--accent)' }}>
        <div className="now-label">Before your day</div>
        <div className="now-title">
          <Sunrise size={26} /> Up at {time(asleep.end)}
        </div>
        <div className="now-meta">Nothing needs you yet – rest up.</div>
      </div>
    );
  }

  if (nextItem) {
    const from = lastEnd ?? dt(now).startOf('day').toMillis();
    return (
      <div className="now-card free" style={{ ['--c' as string]: 'var(--ok)' }}>
        <div className="now-label">Free time</div>
        <div className="now-title">Free for {duration(nextItem.start - now)}</div>
        <Progress start={from} end={nextItem.start} now={now} />
        <div className="now-meta">until {time(nextItem.start)} · then {label(nextItem).title}</div>
      </div>
    );
  }

  return (
    <div className="now-card free" style={{ ['--c' as string]: 'var(--ok)' }}>
      <div className="now-label">That's everything</div>
      <div className="now-title">
        <PartyPopper size={26} /> Nothing else today
      </div>
      {bed && <div className="now-meta">Bed at {time(bed.start)}</div>}
    </div>
  );
}

function DoneButton({ item }: { item: Item }) {
  const mark = useAction(() => api.post(`/blocks/${encodeURIComponent(item.id)}/status`, { status: 'done' }), 'Nice – done ✓');
  return (
    <Button variant="primary" onClick={() => mark.mutate()} loading={mark.isPending}>
      <Check size={16} /> Done
    </Button>
  );
}

function Progress({ start, end, now }: { start: number; end: number; now: number }) {
  const pct = Math.max(0, Math.min(100, ((now - start) / Math.max(1, end - start)) * 100));
  return (
    <div className="now-progress" aria-hidden>
      <div style={{ width: `${pct}%` }} />
    </div>
  );
}

// ───────────────────────────── NEXT ─────────────────────────────

function NextCard({
  item,
  travel,
  next,
  now,
  where,
  onSelect,
}: {
  item: Item;
  travel: Item | null;
  next: NextUp | undefined;
  now: number;
  where: (i: Item) => string | null;
  onSelect: (i: Item) => void;
}) {
  const l = label(item);
  const place = where(item);
  // Use the live / timetable-accurate leave time when the server has one for this item.
  const live = next?.item?.id === item.id ? next : null;
  const leaveAt = live?.journey?.leaveAt ?? live?.live?.leaveAt ?? travel?.start ?? null;
  const travelMin = live?.journey?.minutes ?? live?.live?.travelMin ?? (travel ? Math.round((travel.end - travel.start) / 60_000) : null);
  const mode = travel?.mode ?? live?.mode ?? null;
  const leaveIn = leaveAt != null ? leaveAt - now : null;
  const directions = live?.directionsUrl ?? travel?.directionsUrl ?? null;
  return (
    <div className="next-card" style={{ ['--c' as string]: item.color }} onClick={() => onSelect(item)}>
      <div className="next-head">
        <span className="now-label">Next</span>
        <span className="next-when">in {duration(item.start - now)}</span>
      </div>
      <div className="next-title">
        <span style={{ color: item.color, display: 'inline-flex' }}>
          <ItemIcon item={item} size={20} />
        </span>
        <span className="truncate">{l.title}</span>
      </div>
      <div className="next-meta">
        {time(item.start)}–{time(item.end)}
        {place ? ` · ${place}` : ''}
      </div>
      {leaveAt != null && (
        <div className={`leave-pill ${leaveIn! <= 10 * 60_000 ? 'urgent' : ''}`} onClick={(e) => e.stopPropagation()}>
          <ModeIcon mode={mode} size={16} />
          <span>
            {leaveIn! <= 0 ? 'Leave now' : `Leave at ${time(leaveAt)}`}
            <span className="faint"> · {travelMin} min {MODE_LABEL[mode ?? ''] ?? ''}</span>
          </span>
          {directions && (
            <a className="btn sm" href={directions} target="_blank" rel="noreferrer">
              <Navigation size={14} /> Go
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ───────────────────────────── DAY STRIP ─────────────────────────────

function DayStrip({ things, date, now, onSelect }: { things: Item[]; date: string; now: number | null; onSelect: (i: Item) => void }) {
  const settings = useSettings().data;
  if (!things.length) return null;
  const day = fromISODate(date).startOf('day');
  const hm = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return day.set({ hour: h, minute: m }).toMillis();
  };
  const startMs = Math.min(things[0].start, settings ? hm(settings.planner.dayStart) : things[0].start);
  const endMs = Math.max(things.at(-1)!.end, ...things.map((t) => t.end));
  const span = Math.max(1, endMs - startMs);
  const pos = (ms: number) => `${((ms - startMs) / span) * 100}%`;
  const width = (a: number, b: number) => `${Math.max(0.6, ((b - a) / span) * 100)}%`;
  const firstHour = dt(startMs).startOf('hour').plus({ hours: 1 });
  const ticks: number[] = [];
  for (let t = firstHour; t.toMillis() < endMs; t = t.plus({ hours: 3 })) ticks.push(t.toMillis());

  return (
    <div className="daystrip-wrap">
      <div className="daystrip">
        {now != null && now > startMs && <div className="daystrip-past" style={{ width: pos(Math.min(now, endMs)) }} />}
        {things.map((t) => (
          <button
            key={t.id}
            className={`seg ${t.kind}`}
            style={{ left: pos(t.start), width: width(t.start, t.end), ['--c' as string]: t.color }}
            title={`${time(t.start)} ${shortTitle(t).title}`}
            onClick={() => onSelect(t)}
          />
        ))}
        {now != null && now >= startMs && now <= endMs && <div className="daystrip-now" style={{ left: pos(now) }} />}
      </div>
      <div className="daystrip-ticks">
        {ticks.map((t) => (
          <span key={t} style={{ left: pos(t) }}>
            {time(t)}
          </span>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────── CHUNKS ─────────────────────────────

function Chunks({
  later,
  done,
  isToday,
  now,
  where,
  travelFor,
  onSelect,
  heading,
}: {
  later: Item[];
  done: Item[];
  isToday: boolean;
  now: number;
  where: (i: Item) => string | null;
  travelFor: Map<string, Item>;
  onSelect: (i: Item) => void;
  heading: string;
}) {
  const partOf = (i: Item) => dayPart(dt(i.start).hour);
  const currentPart = dayPart(dt(now).hour);
  const groups = (['morning', 'afternoon', 'evening'] as DayPart[])
    .map((p) => ({ part: p, items: later.filter((i) => partOf(i) === p) }))
    .filter((g) => g.items.length);
  // Today: only the part of the day you're in is open; the rest fold away until you want them.
  const [open, setOpen] = useState<Set<DayPart>>(() => new Set(isToday ? [groups[0]?.part ?? currentPart] : ['morning', 'afternoon', 'evening']));
  const [showDone, setShowDone] = useState(false);
  const toggle = (p: DayPart) => setOpen((s) => new Set(s.has(p) ? [...s].filter((x) => x !== p) : [...s, p]));

  if (!later.length && !done.length) return <div className="empty small">A free day – nothing planned.</div>;

  return (
    <div className="chunks">
      {later.length > 0 && <div className="chunks-heading">{heading}</div>}
      {groups.map((g) => (
        <div key={g.part} className="chunk">
          <button className="chunk-head" onClick={() => toggle(g.part)}>
            {open.has(g.part) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>{DAY_PART_LABEL[g.part]}</span>
            <span className="faint">
              {g.items.length} thing{g.items.length === 1 ? '' : 's'}
            </span>
          </button>
          {open.has(g.part) && g.items.map((it) => <Row key={it.id} it={it} where={where} travel={travelFor.get(it.id)} onSelect={onSelect} />)}
        </div>
      ))}
      {isToday && done.length > 0 && (
        <div className="chunk done">
          <button className="chunk-head" onClick={() => setShowDone(!showDone)}>
            {showDone ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>Earlier today</span>
            <span className="faint">{done.length} past</span>
          </button>
          {showDone && done.map((it) => <Row key={it.id} it={it} where={where} travel={undefined} onSelect={onSelect} past />)}
        </div>
      )}
      {!isToday ? null : later.length === 0 && done.length > 0 ? (
        <div className="faint small" style={{ padding: '4px 12px' }}>
          <Sunrise size={14} /> Nothing else after this.
        </div>
      ) : null}
    </div>
  );
}

function Row({ it, where, travel, onSelect, past }: { it: Item; where: (i: Item) => string | null; travel: Item | undefined; onSelect: (i: Item) => void; past?: boolean }) {
  const l = label(it);
  const place = where(it);
  return (
    <div className={`chunk-row ${past ? 'past' : ''} ${it.status === 'done' ? 'is-done' : ''}`} onClick={() => onSelect(it)} role="button" tabIndex={0}>
      <span className="chunk-time">{time(it.start)}</span>
      <span className="chunk-dot" style={{ background: it.color }} />
      <span className="chunk-text">
        <span className="chunk-title">{l.title}</span>
        {(place || travel) && (
          <span className="chunk-sub">
            {travel ? (
              <span className="chunk-leave">
                <ModeIcon mode={travel.mode} size={12} /> leave {time(travel.start)}
              </span>
            ) : null}
            {place ? <span>{place}</span> : null}
          </span>
        )}
      </span>
      {canTick(it) ? <Tick item={it} /> : <span className="tick-space" />}
    </div>
  );
}
